import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TablePagination,
  Link,
  Stack,
} from '@mui/material'
import { Receipt, OpenInNew, HistoryOutlined } from '@mui/icons-material'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import type { PaymentHistoryDto, PaymentHistoryResponse } from '@api/types/subscription'
import { format } from 'date-fns'

export function BillingHistorySection() {
  const [payments, setPayments] = useState<PaymentHistoryDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPayments()
  }, [page, rowsPerPage])

  const loadPayments = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await subscriptionsApi.getPaymentHistory(page + 1, rowsPerPage)
      setPayments(data.payments)
      setTotalCount(data.totalCount)
    } catch (err) {
      setError('Failed to load payment history')
      console.error('Error loading payment history:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status) {
      case 'Succeeded':
        return 'success'
      case 'Pending':
        return 'info'
      case 'Failed':
        return 'error'
      case 'Refunded':
      case 'PartialRefund':
        return 'warning'
      default:
        return 'default'
    }
  }

  if (isLoading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (payments.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <HistoryOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Payment History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your payment history will appear here once you make a purchase.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Receipt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id} hover>
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(payment.createdAt), 'MMM d, yyyy')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(payment.createdAt), 'h:mm a')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{payment.description || payment.plan.displayName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(payment.periodStart), 'MMM d')} -{' '}
                    {format(new Date(payment.periodEnd), 'MMM d, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {payment.formattedAmount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={payment.statusDisplayName}
                    color={getStatusColor(payment.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  {payment.receiptUrl ? (
                    <Button
                      component={Link}
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      startIcon={<Receipt />}
                      endIcon={<OpenInNew fontSize="small" />}
                    >
                      View
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Paper>
  )
}
